import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { LoadingScreen } from '@/components/layout/LoadingScreen';
import { Button } from '@/components/ui/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Label } from '@/components/ui/Label';
import { GA4_OAUTH_STATE_KEY, ga4RedirectUri } from '@/features/integrations/ga4OAuth';

/**
 * Google Analytics 4 OAuth2 redirect handler. Google redirects here with
 * `code` + `state`. We:
 *   1. Verify `state` against the value stashed in sessionStorage by the
 *      Integrations page (CSRF protection).
 *   2. Forward `code`/`redirect_uri` to the ga4-oauth-callback Edge Function
 *      which exchanges the code for a refresh token, persists it in vault,
 *      and returns the list of GA4 properties the granted account can see.
 *   3. Render a property picker (auto-selects if there's only one).
 *   4. On submit, POST the chosen property to ga4-set-property and trigger
 *      an immediate web-analytics-sync so the dashboard populates.
 *
 * Splitting the flow into "exchange code" + "pick property" rather than
 * doing it all in the auth function lets the user re-pick later from the
 * Integrations page without redoing the OAuth dance.
 */
interface Property {
  propertyId: string;
  displayName: string;
  accountName: string;
}

type Phase =
  | { kind: 'exchanging' }
  | { kind: 'picking'; properties: Property[]; selected: string }
  | { kind: 'finalizing' }
  | { kind: 'error'; message: string };

export function Ga4CallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'exchanging' });
  // React 18 StrictMode mounts effects twice in dev; the OAuth `code` is
  // single-use, so guard against the second invocation eating the error.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error') ?? params.get('error_description');

    if (oauthError) {
      setPhase({ kind: 'error', message: `Google rejected the connection: ${oauthError}` });
      return;
    }
    if (!code) {
      setPhase({ kind: 'error', message: 'Missing OAuth code from Google.' });
      return;
    }

    const expectedState = sessionStorage.getItem(GA4_OAUTH_STATE_KEY);
    if (!expectedState || !state || state !== expectedState) {
      setPhase({
        kind: 'error',
        message:
          'OAuth state mismatch. For your security, please retry the connection from the Integrations page.',
      });
      return;
    }
    sessionStorage.removeItem(GA4_OAUTH_STATE_KEY);

    const redirectUri = ga4RedirectUri();

    (async () => {
      const { data, error: fnError } = await supabase.functions.invoke<{
        ok: boolean;
        properties: Property[];
        error?: string;
      }>('ga4-oauth-callback', {
        body: { code, state, redirect_uri: redirectUri },
      });
      if (fnError) {
        const msg = await extractFunctionErrorMessage(fnError);
        setPhase({ kind: 'error', message: msg });
        return;
      }
      const properties = data?.properties ?? [];
      if (properties.length === 0) {
        setPhase({
          kind: 'error',
          message:
            'The Google account you signed in with has no GA4 properties. Sign in with an account that has Viewer access to your GA4 property and try again.',
        });
        return;
      }
      // Auto-select if there's only one — saves a click on the common
      // "single property" path.
      setPhase({
        kind: 'picking',
        properties,
        selected: properties[0]?.propertyId ?? '',
      });
    })();
  }, [params]);

  const finalize = async (propertyId: string) => {
    setPhase({ kind: 'finalizing' });
    const { error: setErr } = await supabase.functions.invoke('ga4-set-property', {
      body: { property_id: propertyId },
    });
    if (setErr) {
      const msg = await extractFunctionErrorMessage(setErr);
      setPhase({ kind: 'error', message: msg });
      return;
    }

    toast.success('GA4 connected. Backfilling the last 90 days…');

    // Best-effort initial sync. Failures are non-fatal — the hourly cron
    // will retry, and the integration is already marked connected.
    const { error: syncError } = await supabase.functions.invoke('web-analytics-sync', {
      body: {},
    });
    if (syncError) {
      const msg = await extractFunctionErrorMessage(syncError);
      toast.error(`Connected, but initial sync failed: ${msg}`);
    } else {
      toast.success('GA4 sync complete.');
    }

    navigate('/integrations', { replace: true });
  };

  if (phase.kind === 'error') {
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="space-y-4 text-center">
          <h1 className="text-xl font-medium tracking-headline">Connection failed</h1>
          <p className="max-w-lg text-sm text-muted-foreground">{phase.message}</p>
          <Button variant="outline" onClick={() => navigate('/integrations', { replace: true })}>
            Back to Integrations
          </Button>
        </div>
      </div>
    );
  }

  if (phase.kind === 'picking') {
    const { properties, selected } = phase;
    const onSelect = (value: string) =>
      setPhase({ kind: 'picking', properties, selected: value });
    return (
      <div className="flex flex-1 items-center justify-center p-12">
        <div className="w-full max-w-lg space-y-6 rounded-xl border border-border bg-card p-8 shadow-sm">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-fresh-mint" />
              <h1 className="text-xl font-medium tracking-headline">Google connected</h1>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ga4-property">GA4 Property</Label>
            <Select value={selected} onValueChange={onSelect}>
              <SelectTrigger id="ga4-property">
                <SelectValue placeholder="Choose a property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.propertyId} value={p.propertyId}>
                    <span className="font-medium">{p.displayName}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {p.accountName} · ID {p.propertyId}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/integrations', { replace: true })}
            >
              Cancel
            </Button>
            <Button onClick={() => finalize(selected)} disabled={!selected}>
              Connect &amp; sync
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
}

async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : 'Edge Function call failed';
  const ctx = (error as { context?: Response }).context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const cloned = ctx.clone();
    const body = (await cloned.json()) as { error?: string };
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}
